import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'

import AdminIntegrationClickUpCustomFieldsConfig from '@/components/admin/settings/Integrations/config/ClickUp/AdminIntegrationClickUpCustomFieldsConfig'
import AlertDialog from '@/components/shared/ui/AlertDialog'
import Button from '@/components/shared/ui/Button'
import Checkbox from '@/components/shared/ui/Checkbox/Checkbox'
import Dialog from '@/components/shared/ui/Dialog'
import {
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/shared/ui/Dialog/Dialog'
import EmptyState from '@/components/shared/ui/EmptyState'
import { SpinnerIcon } from '@/components/shared/ui/Icons'
import Spinner from '@/components/shared/ui/Loader'
import { useTranslations } from '@/hooks/useTranslations'
import {
  getIntegrationConfig,
  updateIntegrationConfig,
} from '@/models/Integration'
import type {
  IClickUpConfigButtonProps,
  IClickUpConfigUpdatePayload,
  IClickUpIntegrationConfig,
  IClickUpPushRule,
} from '@/types/integration/click_up'
import type { IStatusSyncRule } from '@/types/integration/statusSync'
import toaster from '@/utils/toast'

import AdminIntegrationClickUpPushRule from './ClickUp/AdminIntegrationClickUpPushRule'
import AdminIntegrationClickUpSyncStatus from './ClickUp/AdminIntegrationClickUpSyncStatus'

export default function ClickUpConfiguration({
  onDisconnect,
  integrationMapping,
}: IClickUpConfigButtonProps) {
  const t = useTranslations(`integrations.clickUp`)
  const router = useRouter()
  const { provider, integrated } = router.query
  const [configData, setConfigData] = useState<IClickUpIntegrationConfig>()
  const [isLoading, setIsLoading] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfig, setShowConfig] = useState(false)
  const [autoPushRule, setAutoPushRule] = useState<IClickUpPushRule>()

  const fetchConfig = () => {
    if (!configData) {
      setIsLoading(true)
    } else {
      setIsSyncing(true)
    }
    if (isSubmitting) {
      setIsSubmitting(false)
    }

    getIntegrationConfig('click_up')
      .then((data: IClickUpIntegrationConfig) => {
        if (configData) {
          setConfigData({ ...configData, ...data })
        } else {
          setConfigData(data)
        }
        setAutoPushRule(data.push_rule)
      })
      .catch((err) => toaster.error(err.message))
      .finally(() => {
        setIsLoading(false)
        setIsSyncing(false)
      })
  }

  const handleUpdateConfig = (config: IClickUpConfigUpdatePayload) => {
    setIsSubmitting(true)
    if (!configData) return null
    return updateIntegrationConfig('click_up', configData.id.toString(), config)
      .then((data) => {
        setConfigData(data)
        setAutoPushRule(data.push_rule)
        toaster.success({
          message: t(`messages.successfullySaved`, {
            ignorePrefix: true,
          }),
        })
      })
      .catch((error) => toaster.error({ message: error.message }))
      .finally(() => setIsSubmitting(false))
  }

  const handleRemoveSyncRule = (newSyncRules: IStatusSyncRule[]) => {
    const config = {
      push_rule: autoPushRule,
      rules: newSyncRules,
    } as IClickUpConfigUpdatePayload
    return handleUpdateConfig(config)
  }

  const handleAddSyncRule = (newSyncRules: IStatusSyncRule[]) => {
    const newConfigData = {
      ...configData,
      push_rule: autoPushRule,
      sync_rules: newSyncRules,
    } as IClickUpIntegrationConfig
    setConfigData(newConfigData)
  }

  const handleSubmit = () => {
    const config = {
      push_rule: autoPushRule,
      rules: configData?.sync_rules || [],
    } as IClickUpConfigUpdatePayload
    return handleUpdateConfig(config)
  }

  const handlePushRuleChange = (newPushRule: IClickUpPushRule) => {
    setAutoPushRule(newPushRule)
  }

  const handleToggleCustomStatus = (checked: boolean) => {
    if (!configData) return null
    return updateIntegrationConfig('click_up', configData.id.toString(), {
      sync_by_custom_state: checked,
    }).then((updatedSyncData) => {
      setConfigData(updatedSyncData)
    })
  }

  useEffect(() => {
    if (provider === 'click_up' && integrated === 'true') {
      setShowConfig(true)
    }
  }, [])

  useEffect(() => {
    if (showConfig) fetchConfig()
  }, [showConfig])

  useEffect(() => {
    setAutoPushRule(configData?.push_rule)
  }, [configData])

  const renderLoadingOrEmpty = () => {
    if (isLoading) return <Spinner />

    return (
      <EmptyState
        title={t('messages.something_went_wrong', { ignorePrefix: true })}
      />
    )
  }

  return (
    <Dialog
      open={showConfig}
      onClose={() => {
        setAutoPushRule(configData?.push_rule)
        setShowConfig(false)
      }}
    >
      <DialogTrigger asChild>
        <Button size='xs' onClick={() => setShowConfig(true)}>
          {t('buttons.configure', { ignorePrefix: true })}
        </Button>
      </DialogTrigger>
      <DialogContent size='lg' backdrop>
        <DialogTitle>
          <div className='flex items-center justify-between'>
            {t(`configure.title`)}
            <div className='flex items-center gap-2'>
              {isSyncing && <SpinnerIcon className='h-4 w-4 animate-spin' />}
              {!!configData && (
                <Checkbox
                  isSwitch
                  defaultChecked={configData?.sync_by_custom_state}
                  onCheckedChange={handleToggleCustomStatus}
                  alertTexts={{
                    title: 'Are sure you want to use custom states?',
                    description:
                      'All your sync rule will be used to sync custom statuses.',
                  }}
                  disabledAlertTexts={{
                    title: 'Use parent status for sync?',
                    description:
                      'All your sync rule will be used to sync parent statuses.',
                  }}
                  label='Use custom states'
                />
              )}
            </div>
          </div>
        </DialogTitle>
        <>
          {isLoading || !configData || !integrationMapping || !autoPushRule ? (
            renderLoadingOrEmpty()
          ) : (
            <>
              <AdminIntegrationClickUpPushRule
                onChange={handlePushRuleChange}
                pushRule={autoPushRule}
                configData={configData.config}
                isSubmitting={isSubmitting}
                onSave={handleSubmit}
                useCustomStates={configData.sync_by_custom_state}
              />
              <AdminIntegrationClickUpSyncStatus
                configData={configData}
                onRemove={handleRemoveSyncRule}
                onAdd={handleAddSyncRule}
              />
              <AdminIntegrationClickUpCustomFieldsConfig
                configData={configData}
              />
            </>
          )}
          <div className='mb-2 mt-4 flex w-full justify-end space-x-2'>
            <AlertDialog
              type='danger'
              confirmText={t(`buttons.disconnect`, {
                ignorePrefix: true,
              })}
              title={t(`disconnectConfirmationTitle`)}
              description={t(`disconnectConfirmationMessage`)}
              onConfirm={onDisconnect}
            >
              <Button
                variant='danger'
                size='xs'
                disabled={isSubmitting || isLoading}
              >
                {t(`buttons.disconnect`, {
                  ignorePrefix: true,
                })}
              </Button>
            </AlertDialog>
          </div>
        </>
        <DialogClose />
      </DialogContent>
    </Dialog>
  )
}
